import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useStore } from '@/lib/store';
import { SAMPLES, themeColorByName } from '@/data/samples';
import { toast } from 'sonner';

export default function UseCases() {
  const { runExtraction } = useStore();

  const onSample = (s) => {
    toast.success(`Sample loaded — ${s.category}`, {
      description: s.tagline || s.source || 'Sample ready',
    });
    runExtraction({ sampleId: s.id });
    setTimeout(() => {
      document.getElementById('console')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const sampleTags = (s) =>
    s.tags?.length
      ? s.tags
      : [...new Set((s.highlights || []).map((h) => h.themeName).filter(Boolean))];

  return (
    <section id="use-cases" data-testid="use-cases-section" className="py-16 sm:py-24">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Header — asymmetric two-column */}
        <div className="grid md:grid-cols-[1fr_1.1fr] gap-6 md:items-end mb-12 reveal">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/45 mb-3">
              — MADE FOR
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Three recordings.{' '}
              <span className="text-grad">One instinct for signal.</span>
            </h2>
          </div>
          <p className="text-white/60 text-sm sm:text-base md:justify-self-end max-w-lg">
            Three ready-made sample recordings — podcast, research, and discourse.
            Click any card to run a demo and see how SIMNOPSIS finds the signal in each.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6 reveal">
          {SAMPLES.map((s, idx) => (
            <button
              key={s.id}
              className="group relative flex flex-col overflow-hidden rounded-[var(--radius-lg)] border border-white/8 text-left transition-transform duration-500 hover:-translate-y-1 hover:border-white/15 bg-[var(--panel)]"
              style={{ minHeight: 420 }}
              onClick={() => onSample(s)}
              data-testid={`usecase-card-${s.id}`}
            >
              {/* Photo area — top portion */}
              <div className="relative h-44 sm:h-48 overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${s.photo})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--panel)]" />

                {/* Sample badge + category */}
                <div className="absolute top-4 left-4 flex flex-wrap items-center gap-1.5">
                  <span
                    className="rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-[#7EE06D]"
                    style={{
                      background: 'rgba(126,224,109,0.12)',
                      border: '1px solid rgba(126,224,109,0.35)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                    }}
                  >
                    Sample
                  </span>
                  <span className="glass-panel-soft !rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-white/85">
                    {String(idx + 1).padStart(2, '0')} · {s.category}
                  </span>
                </div>

                {/* Arrow icon top-right */}
                <div className="absolute top-4 right-4">
                  <span className="w-8 h-8 rounded-full glass-panel-soft flex items-center justify-center text-white/80 group-hover:text-white transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5">
                    <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </div>

              {/* Content area — below photo */}
              <div className="flex flex-col flex-1 p-5 pt-3">
                <h3 className="font-display font-semibold text-xl leading-snug text-white">
                  {s.headline || s.tagline}
                </h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed line-clamp-2">
                  {s.description}
                </p>

                {/* Theme tags at bottom — glass pills tinted by theme color */}
                <div className="mt-auto pt-4 flex flex-wrap gap-1.5">
                  {sampleTags(s).map(tag => {
                    const c = themeColorByName(tag);
                    return (
                      <span
                        key={tag}
                        className="pill !text-[9px] !tracking-wider"
                        style={{
                          color: c,
                          background: `${c}18`,
                          border: `1px solid ${c}44`,
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)',
                          boxShadow: `inset 0 1px 0 ${c}22`,
                        }}
                      >
                        {tag}
                      </span>
                    );
                  })}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
